// src/journey/WhatWeNeedCard.tsx
//
// BekalNanti — P1 What We Don't Know Yet Card
// Explicitly identifies missing information preventing calculation.

import React from 'react'
import { MissingInfoItem } from './types'
import { HelpCircle, AlertCircle, ArrowRight } from 'lucide-react'

interface WhatWeNeedCardProps {
  missingItems: MissingInfoItem[]
  onAddressNextClick: () => void
}

export const WhatWeNeedCard: React.FC<WhatWeNeedCardProps> = ({
  missingItems,
  onAddressNextClick,
}) => {
  return (
    <div className="rounded-2xl border border-amber-200/90 bg-white p-4 shadow-sm text-left space-y-4">
      {/* Card Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
            <HelpCircle size={16} />
          </div>
          <div>
            <span className="text-[10px] font-bold tracking-wider text-amber-700 uppercase">
              Informasi yang Masih Dibutuhkan
            </span>
            <h3 className="text-sm font-bold text-slate-900 leading-tight">
              Yang Belum Diketahui BekalNanti
            </h3>
          </div>
        </div>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
          {missingItems.length} Data Kunci
        </span>
      </div>

      <p className="text-xs text-slate-500 leading-relaxed">
        BekalNanti tidak menebak-nebak angka atau memberikan rekomendasi kosong. Data berikut diperlukan sebelum Mesin Kalkulasi &amp; Reality Check dapat dijalankan secara akurat.
      </p>

      {/* Missing Items List */}
      <div className="space-y-2.5 pt-1">
        {missingItems.map((item) => (
          <div
            key={item.id}
            className="p-3 rounded-xl bg-amber-50/40 border border-amber-200/60 text-xs space-y-1.5"
          >
            <div className="flex items-start gap-2">
              <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h4 className="font-bold text-slate-900 leading-snug">{item.title}</h4>
                <p className="text-slate-600 text-[11px] mt-0.5 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>

            <div className="pl-5 pt-1 text-[11px] text-amber-900 font-medium border-t border-amber-200/40 mt-1">
              <span className="text-amber-700 font-bold">Mengapa penting:</span> {item.whyItMatters}
            </div>
          </div>
        ))}
      </div>

      {/* Guidance Note */}
      <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-600 flex items-center justify-between">
        <span>Kita tidak perlu tahu semuanya sekaligus untuk mulai melangkah.</span>
        <button
          type="button"
          onClick={onAddressNextClick}
          className="text-xs font-semibold text-[#0EA5A4] hover:text-[#0F766E] flex items-center gap-1 transition-colors cursor-pointer ml-2 flex-shrink-0"
        >
          <span>Langkah Berikutnya</span>
          <ArrowRight size={13} />
        </button>
      </div>
    </div>
  )
}
